// SubNav.jsx
import React, { useState, useRef, useEffect } from 'react';
import { BiSolidMessageRoundedDetail } from "react-icons/bi";
import { FaBell } from "react-icons/fa";
import { FiLogOut } from "react-icons/fi";
import { RxHamburgerMenu } from "react-icons/rx";
import { IoSettingsOutline } from 'react-icons/io5';
import { BiHelpCircle } from 'react-icons/bi';
import { MdOutlineFeedback } from 'react-icons/md';
import { BsMoon } from 'react-icons/bs';
import profile from '../assets/img/defaultProfilePic.png';
import logo from '../assets/img/logomini.png';
import styles from "../styles/subNav.css";
import { NavLink } from "react-router-dom";
import Dictaphone from "./speechRecognition";
import Notifications from "./notifications";
import { FaDiagramProject } from "react-icons/fa6";
import { useNavigate } from 'react-router-dom';
import { baseUrl } from '../config';

export default function SubNav({ hideMessenger = false }) {
    const [isProfileOpen, setIsProfileOpen] = useState(false);
    const dropdownRef = useRef(null);
    const [isProjectOpen, setIsProjectOpen] = useState(false);
    const projectDropdownRef = useRef(null);
    const [activeProjects, setActiveProjects] = useState([]);
    const navigate = useNavigate();
    const [activeUser, setActiveUser] = useState(null);
    const [userDetails, setUserDetails] = useState({
        username: 'User',
        email: 'user@example.com',
        firstName: 'U',
        lastName: '',
        profilePicture: '',
        customer_id: '',
        user_type: ''
    });
    const [isLoading, setIsLoading] = useState(true);

    /* On Load functions*/
    useEffect(() => {
        setActiveProjects([]);
        initializeUserData();

        // ✅ Listen for profile updates
        const handleProfileUpdate = () => {
            initializeUserData();
        };

        window.addEventListener('profileUpdated', handleProfileUpdate);

        return () => {
            window.removeEventListener('profileUpdated', handleProfileUpdate);
        };
    }, []);

    /* Initialize user data */
    async function initializeUserData() {
        try {
            setIsLoading(true);
            await loadUserDetails();
            await getUsersActiveProjects();
        } catch (error) {
        } finally {
            setIsLoading(false);
        }
    }

    /* Get user details - ✅ FIXED: Now uses token */
    async function loadUserDetails() {
        try {
            const token = localStorage.getItem("access_token");
            if (!token) {
                navigate("/login");
                return;
            }
            const userResponse = await fetch(`${baseUrl}/getUserById`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
            });
            if (userResponse.ok) {
                const userData = await userResponse.json();
                const username = userData.username || 'User';
                const email = userData.email || 'user@example.com';
                const nameParts = username.split(' ');
                const firstName = userData.first_name || nameParts[0] || 'U';
                const lastName = userData.last_name || (nameParts.length > 1 ? nameParts[nameParts.length - 1] : '');

                setUserDetails({
                    username: username,
                    email: email,
                    firstName: firstName,
                    lastName: lastName,
                    profilePicture: userData.profile_picture || '',
                    customer_id: userData.customer_id || '',
                    user_type: userData.user_type || ''
                });
            } else if (userResponse.status === 401) {
                navigate("/login");
            } else {
                const errorText = await userResponse.text();
                setUserDetails({
                    username: 'User',
                    email: 'user@example.com',
                    firstName: 'U',
                    lastName: '',
                    profilePicture: '',
                    customer_id: '',
                    user_type: ''
                });
            }
        } catch (error) {
            setUserDetails({
                username: 'User',
                email: 'user@example.com',
                firstName: 'U',
                lastName: '',
                profilePicture: '',
                customer_id: '',
                user_type: ''
            });
        }
    }

    /* Get user's active projects - ✅ FIXED: Now sends user_id header */
    async function getUsersActiveProjects() {
        try {
            setActiveProjects([]);

            const token = localStorage.getItem("access_token");
            if (!token) {
                return;
            }

            // ✅ First, get the user ID from token
            const userIdResponse = await fetch(`${baseUrl}/getUserIDByToken`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                },
            });
            if (!userIdResponse.ok) {
                if (userIdResponse.status === 401) {
                    navigate("/login");
                }
                return;
            }

            const userIdData = await userIdResponse.json();
            const userId = userIdData.user_id;
            // ✅ Now fetch projects with user_id in header
            const projectsRequest = await fetch(`${baseUrl}/getProjectsByUserId`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                    'user_id': String(userId),  // ← This was missing!
                },
            });
            if (projectsRequest.ok) {
                const response = await projectsRequest.json();
                let projects;
                if (Array.isArray(response)) {
                    projects = response;
                } else if (response && Array.isArray(response.projects)) {
                    projects = response.projects;
                } else {
                    projects = [];
                }
                setActiveProjects(projects);
            } else if (projectsRequest.status === 401) {
                navigate("/login");
            } else {
                const errorText = await projectsRequest.text();
                setActiveProjects([]);
            }
        } catch (error) {
            setActiveProjects([]);
        }
    }

    /* Generate user avatar with initials or profile picture */
    const generateAvatar = () => {
        if (userDetails.profilePicture) {
            return (
                <img
                    src={userDetails.profilePicture}
                    alt="Profile"
                    style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        borderRadius: '50%'
                    }}
                />
            );
        }

        const firstInitial = userDetails.firstName.charAt(0).toUpperCase();
        const lastInitial = userDetails.lastName.charAt(0).toUpperCase();
        return firstInitial + lastInitial;
    };

    /* render function for each active project a user is a part of */
    const ProjectList = () => {
        if (!Array.isArray(activeProjects) || activeProjects.length === 0) {
            return <div className="no-projects" key="no-projects">No projects found</div>;
        }

        return activeProjects.map(project => (
            <a
                href={"/toolselector/" + project.project_id}
                key={project.project_id}
                className="project-item"
            >
                {project.title && project.title !== "" ? project.title : "Unnamed Project"}
            </a>
        ));
    };

    // Close dropdown when clicking outside
    useEffect(() => {
        function handleClickOutside(event) {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsProfileOpen(false);
            }
            if (projectDropdownRef.current && !projectDropdownRef.current.contains(event.target)) {
                setIsProjectOpen(false);
            }
        }

        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    // Handle logout
    const handleLogout = () => {
        localStorage.removeItem("access_token");
        localStorage.removeItem("user_dashboard");
        navigate("/login");
    };

    return (
        <nav id='loginNav'>
            <NavLink to="/">
                <img src={logo} alt="Producers Buddy Logo" className="logo" />
            </NavLink>

            <Dictaphone id="subNavMic" />

            <div id='profile'>
                {!hideMessenger && (
                    <NavLink to='/messenger'>
                        <BiSolidMessageRoundedDetail id="messengerIcon" />
                    </NavLink>
                )}

                <div className="project-dropdown" ref={projectDropdownRef}>
                    <div
                        className="project-trigger"
                        onClick={() => setIsProjectOpen(!isProjectOpen)}
                    >
                        <FaDiagramProject id="projectIcon" />
                    </div>

                    {isProjectOpen && (
                        <div className="project-menu">
                            <div className="project-menu-header">
                                <h6>Your Projects</h6>
                            </div>
                            <div className="project-menu-items">
                                <div className="new-project-button" key="new-project-btn">
                                    <a href="/starter" className="start-new-project">Start New Project</a>
                                </div>
                                <ProjectList />
                            </div>
                            {ProjectList && ProjectList.length > 0 ? ProjectList : (
                                <div className="no-projects">No projects found</div>
                            )}
                        </div>
                    )}
                </div>

                <details>
                    <summary><FaBell id='navBellIcon' /></summary>
                    <Notifications />
                </details>

                {/* Profile Dropdown */}
                <div className="profile-dropdown" ref={dropdownRef}>
                    <div
                        className="profile-trigger"
                        onClick={() => setIsProfileOpen(!isProfileOpen)}
                    >
                        {/* User Avatar with Initials or Profile Picture */}
                        <div className="user-avatar">
                            {isLoading ? '...' : generateAvatar()}
                        </div>
                    </div>

                    {isProfileOpen && (
                        <div className="dropdown-menus">
                            {/* Profile Header */}
                            <div className="dropdown-header">
                                <NavLink to="/settings" className="profile-info">
                                    <div className="user-avatar-large">
                                        {isLoading ? '...' : generateAvatar()}
                                    </div>
                                    <div className="user-info">
                                        <h6>{isLoading ? 'Loading...' : userDetails.username}</h6>
                                        <p>{isLoading ? 'Loading...' : userDetails.email}</p>
                                        {userDetails.user_type && (
                                            <small style={{ color: '#666', fontSize: '0.8em' }}>
                                                {userDetails.user_type}
                                            </small>
                                        )}
                                    </div>
                                </NavLink>
                            </div>

                            {/* Menu Items */}
                            <div className="dropdown-itemso">
                                <NavLink to="/SettingPrivicy">
                                    <IoSettingsOutline />
                                    <span>Settings & Privacy</span>
                                </NavLink>

                                <NavLink to="/Contactus">
                                    <BiHelpCircle />
                                    <span>Help & Support</span>
                                </NavLink>

                                <NavLink to="/feedback">
                                    <MdOutlineFeedback />
                                    <span>Give Feedback</span>
                                </NavLink>

                                <div
                                    onClick={handleLogout}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        padding: '12px 16px',
                                        cursor: 'pointer',
                                        textDecoration: 'none',
                                        color: 'inherit'
                                    }}
                                >
                                    <FiLogOut />
                                    <span style={{ marginLeft: '8px' }}>Log Out</span>
                                </div>
                            </div>

                            {/* Footer Links */}
                            <div className="dropdown-footers">
                                <NavLink to="/privacy">Privacy</NavLink> ·
                                <NavLink to="/terms">Terms</NavLink> ·
                                <NavLink to="/advertising">Advertising</NavLink> ·
                                <NavLink to="/cookies">Cookies</NavLink> ·
                                <NavLink to="/more">More</NavLink>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </nav >
    );
}
